import React from "react";
import { Box, Typography } from "@mui/material";
import { WarningTriangleIcon } from "design-system/icons";
import MoovsTooltip from "components/MoovsTooltip";
import {
  errorRed,
  warningYellow,
  successGreen,
  grayDark,
} from "design-system/colors";

type RiskScoreDisplayProps = {
  riskScore?: number | null;
  riskEvaluation?: string | null;
  showIcon?: boolean;
  variant?: "compact" | "full";
};

const getRiskScoreColor = (score: number): string => {
  if (score >= 70) {
    return errorRed;
  }
  if (score >= 40) {
    return warningYellow;
  }
  return successGreen;
};

const getRiskScoreTooltip = (score: number): string => {
  if (score >= 70) {
    return `High risk payment (${score}/100). This payment has major fraud indicators.`;
  }
  if (score >= 40) {
    return `Medium risk payment (${score}/100). This payment has minor fraud indicators.`;
  }
  return `Low risk payment (${score}/100). This payment appears legitimate.`;
};

const getRiskScoreLabel = (score: number): string => {
  if (score >= 70) {
    return "High Risk";
  }
  if (score >= 40) {
    return "Medium Risk";
  }
  return "Low Risk";
};

function RiskScoreDisplay(props: RiskScoreDisplayProps) {
  const { riskScore, showIcon = true, variant = "full" } = props;

  if (riskScore == null) {
    return (
      <Typography variant="body2" color={grayDark}>
        N/A
      </Typography>
    );
  }

  const color = getRiskScoreColor(riskScore);
  const tooltipText = getRiskScoreTooltip(riskScore);
  const isHighRisk = riskScore >= 40;

  if (variant === "compact") {
    return (
      <MoovsTooltip tooltipText={tooltipText}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography
            variant="body2"
            color={color}
            fontWeight={riskScore >= 70 ? "bold" : "normal"}
          >
            {riskScore}
          </Typography>
          {showIcon && isHighRisk && (
            <WarningTriangleIcon color={color} size="small" />
          )}
        </Box>
      </MoovsTooltip>
    );
  }

  return (
    <MoovsTooltip tooltipText={tooltipText}>
      <Box display="flex" alignItems="center" gap={1}>
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <Typography
            variant="body2"
            color={color}
            fontWeight={riskScore >= 70 ? "bold" : "normal"}
          >
            {riskScore}/100
          </Typography>
          <Typography variant="caption" color={color} fontSize="0.7rem">
            {getRiskScoreLabel(riskScore)}
          </Typography>
        </Box>
        {showIcon && isHighRisk && <WarningTriangleIcon color={color} />}
      </Box>
    </MoovsTooltip>
  );
}

export default RiskScoreDisplay;
